import React from 'react';
import { useNavigate } from 'react-router-dom';
import { useInView } from '../../hooks/useInView';
import './ServicesSection.css';

// SVG Icon Components
const TrophyIcon: React.FC<{ className?: string }> = ({ className }) => (
  <svg className={className} viewBox="0 0 64 64" fill="none" xmlns="http://www.w3.org/2000/svg">
    <path d="M22 16H42V24C42 30.6274 37.5228 36 32 36C26.4772 36 22 30.6274 22 24V16Z" stroke="currentColor" strokeWidth="2.4" strokeLinecap="round" strokeLinejoin="round"/>
    <path d="M22 20H18C15.7909 20 14 21.7909 14 24V24C14 28.4183 17.5817 32 22 32" stroke="currentColor" strokeWidth="2.4" strokeLinecap="round"/>
    <path d="M42 20H46C48.2091 20 50 21.7909 50 24V24C50 28.4183 46.4183 32 42 32" stroke="currentColor" strokeWidth="2.4" strokeLinecap="round"/>
    <path d="M32 36V46" stroke="currentColor" strokeWidth="2.4" strokeLinecap="round"/>
    <rect x="25" y="46" width="14" height="4" rx="2" stroke="currentColor" strokeWidth="2.4"/>
    <path d="M20 54H44" stroke="currentColor" strokeWidth="2.4" strokeLinecap="round"/>
    <circle cx="32" cy="24" r="2.5" fill="currentColor" opacity="0.22"/>
  </svg>
);

const MedalIcon: React.FC<{ className?: string }> = ({ className }) => (
  <svg className={className} viewBox="0 0 64 64" fill="none" xmlns="http://www.w3.org/2000/svg">
    <path d="M24 10H30L28 24H22L24 10Z" stroke="currentColor" strokeWidth="2.2" strokeLinejoin="round"/>
    <path d="M34 10H40L42 24H36L34 10Z" stroke="currentColor" strokeWidth="2.2" strokeLinejoin="round"/>
    <circle cx="32" cy="34" r="12" stroke="currentColor" strokeWidth="2.4"/>
    <path d="M32 28L33.7 31.4L37.5 32L34.8 34.7L35.4 38.5L32 36.8L28.6 38.5L29.2 34.7L26.5 32L30.3 31.4L32 28Z" fill="currentColor" opacity="0.22"/>
    <path d="M24.5 43.5L21 54L28 49.5L32 56L36 49.5L43 54L39.5 43.5" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round"/>
  </svg>
);

const EngravingIcon: React.FC<{ className?: string }> = ({ className }) => (
  <svg className={className} viewBox="0 0 64 64" fill="none" xmlns="http://www.w3.org/2000/svg">
    <rect x="12" y="34" width="40" height="14" rx="2" stroke="currentColor" strokeWidth="2.3"/>
    <path d="M20 41H30" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round"/>
    <path d="M34 41H44" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round"/>
    <path d="M46 18L52 24L39 37L33 37L33 31L46 18Z" stroke="currentColor" strokeWidth="2.3" strokeLinejoin="round"/>
    <path d="M43 21L49 27" stroke="currentColor" strokeWidth="2.3" strokeLinecap="round"/>
    <circle cx="21" cy="41" r="1.4" fill="currentColor" opacity="0.4"/>
    <circle cx="43" cy="41" r="1.4" fill="currentColor" opacity="0.4"/>
  </svg>
);

const CrystalIcon: React.FC<{ className?: string }> = ({ className }) => (
  <svg className={className} viewBox="0 0 64 64" fill="none" xmlns="http://www.w3.org/2000/svg">
    <path d="M20 12H44L40 27C39.1119 30.5523 35.912 33 32.25 33H31.75C28.088 33 24.8881 30.5523 24 27L20 12Z" stroke="currentColor" strokeWidth="2.3" strokeLinejoin="round"/>
    <path d="M26 20H38" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round"/>
    <path d="M32 33V46" stroke="currentColor" strokeWidth="2.3" strokeLinecap="round"/>
    <path d="M24 52H40" stroke="currentColor" strokeWidth="2.3" strokeLinecap="round"/>
    <path d="M44 14L46 17L49 19L46 21L44 24L42 21L39 19L42 17L44 14Z" fill="currentColor" opacity="0.26"/>
  </svg>
);

const ServicesSection: React.FC = () => {
  const navigate = useNavigate();
  const { ref: servicesRef, isInView: servicesInView } = useInView<HTMLElement>({ threshold: 0.22 });

  const services = [
    {
      icon: TrophyIcon,
      title: 'Trophies',
      description: 'Bespoke pieces for wins and milestones.',
      link: '/shop?category=Trophies'
    },
    {
      icon: MedalIcon,
      title: 'Medals & Awards',
      description: 'Premium medals for events and competitions.',
      link: '/shop?category=Medals'
    },
    {
      icon: EngravingIcon,
      title: 'Engraving',
      description: 'Personalized engraving with clean finishing.',
      link: '/shop?category=Personalisation'
    },
    {
      icon: CrystalIcon,
      title: 'Crystal',
      description: 'Elegant Galway Crystal for gifts and occasions.',
      link: '/shop?category=Galway Crystal'
    }
  ];

  return (
    <section ref={servicesRef} className={`services-section ${servicesInView ? 'services-visible' : ''}`}>
      <div className="services-container">
        <div className="services-header">
          <h2 className="services-title">Our Services</h2>
          <p className="services-subtitle">
            From custom trophies to elegant crystal, we provide comprehensive solutions for all your recognition needs
          </p>
        </div>
        <div className="services-grid">
          {services.map((service, index) => {
            const IconComponent = service.icon;
            return (
              <div 
                key={index} 
                className="service-card"
                onClick={() => navigate(service.link)}
                style={{ ['--service-index' as any]: index }}
              >
                <div className="service-icon">
                  <IconComponent className="service-icon-svg" />
                </div>
                <h3 className="service-title">{service.title}</h3>
                <p className="service-description">{service.description}</p>
              </div>
            );
          })}
        </div>
      </div>
    </section>
  );
};

export default ServicesSection;

